import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { Avatar } from 'components/Avatar';
import { Txt } from 'components/Txt';
import { useScreenSize } from 'hooks/useScreenSize';
import { useEsc } from 'hooks/useEsc';
import { pluralise, pluraliseS } from 'lib/pluralise';
import { LayoutAnimation, View } from 'react-native';
import { ButtonToggleGroup } from 'components/ButtonToggleGroup';
import {
  EngageFlatIsHMO,
  EngageFlatIsCommerciallyOwned,
  TaskStatusType,
  BlockManagerAssessmentSummary,
} from 'types/DTOs/BlockManagerDTOs';
import {
  filterArrayEmpty,
  formatDate,
  rbAddReferencePrefix,
} from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  doEngageFlatChangeOccupationStatus,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { Button } from 'components/Button';
import {
  EngageFlatContact,
  EngageFlatIssues,
  EngageFlatScheduledVisit,
  FlatOccupationStatus,
} from 'types/Engage';
import { ScreenSize, UI_DATE_FORMAT_SHORT } from 'lib/constants';
import { EngageFlatModalCommunicationsPage } from './EngageFlatModalCommunicationsPage';
import {
  formatScheduledVisitSlotTimeRange,
  getScheduledVisitDateRangeText,
  getScheduledVisitSlotDayGroups,
} from './scheduledVisitUtils';
import { EngageContactCard } from './Components/EngageContactCard';
import { selectEngageFlat } from 'store/remote/engageSelectors';
import {
  doEngageArchiveContact,
  doEngageFlatAssessmentSummariesLoad,
  doEngageFlatArchivedContactsLoad,
  doEngageFlatChangeIsHMO,
  doEngageFlatChangeIsCommerciallyOwned,
  doEngageUnarchiveContact,
} from 'store/remote/engageActions';
import { GapSmall } from 'components/GapSmall';
import { PageHeader } from 'components/PageHeader';
import { ScrollView } from 'components/ScrollView';
import { IconButton } from 'components/IconButton';
import { Gap } from 'components/Gap';
import { Pill } from 'components/Pill';

type FlatIssueDetailKey = Exclude<
  keyof EngageFlatIssues,
  'hasOwnerIssues' | 'hasResidentIssues'
>;

const FlatIssueDetailContent: Record<
  FlatIssueDetailKey,
  { label: string; message: string }
> = {
  hasNoResidents: {
    label: 'Resident Information Required',
    message: 'Information about the residents living in this flat is required.',
  },
  hasNoOwners: {
    label: 'Owner Information Required',
    message: 'Information about the owners of this flat is required.',
  },
  isMissingResidents: {
    label: 'Missing Resident Contacts',
    message: "There may be other residents but their details aren't recorded.",
  },
  hasExpiredResidentConfirmations: {
    label: 'Resident Information Needs Re-confirming',
    message:
      'All contact information needs to be re-confirmed every 12 months.',
  },
  hasExpiredOwnerConfirmations: {
    label: 'Owner Information Needs Re-confirming',
    message:
      'All contact information needs to be re-confirmed every 12 months.',
  },
  hasEmailIssues: {
    label: 'Email Delivery Issues',
    message:
      'Some contacts have email delivery problems (bounced, rejected or delayed).',
  },
  hasSurveyResponseOverdue: {
    label: 'Contact Survey Response Overdue',
    message: 'A contact survey has been sent but the response is now overdue.',
  },
  requiresContactSurvey: {
    label: 'Contact Survey Required',
    message:
      'A contact survey should be sent to gather current information about the owners and residents of this flat.',
  },
};

const FlatIssueDetailOrder: FlatIssueDetailKey[] = [
  'hasNoResidents',
  'hasNoOwners',
  'isMissingResidents',
  'hasExpiredResidentConfirmations',
  'hasExpiredOwnerConfirmations',
  'hasEmailIssues',
  'hasSurveyResponseOverdue',
  'requiresContactSurvey',
];

const ScheduledVisitCardRow: React.FC<{
  scheduledVisit: EngageFlatScheduledVisit;
  handleOpenScheduledVisitData: (id: string) => void;
}> = ({ scheduledVisit, handleOpenScheduledVisitData }) => {
  const respondedSlots = getScheduledVisitSlotDayGroups(
    scheduledVisit.slots.filter((slot) => slot.hasResponse),
  ).map((dayGroup) => ({
    id: dayGroup.key,
    dateText: formatDate(dayGroup.slotDate, UI_DATE_FORMAT_SHORT),
    timesText: dayGroup.slots
      .map((slot) =>
        formatScheduledVisitSlotTimeRange(slot.startDateTime, slot.endDateTime),
      )
      .join(', '),
  }));
  return (
    <CardRow
      key={scheduledVisit.id}
      onPress={() => handleOpenScheduledVisitData(scheduledVisit.id)}
    >
      <Txt weight="medium">
        {getScheduledVisitDateRangeText(scheduledVisit)}
      </Txt>
      <Txt size="xsmall">
        {scheduledVisit.isUnavailable ? (
          <Txt size="xsmall">Unavailable</Txt>
        ) : respondedSlots.length ? (
          respondedSlots.map((slot, index) => (
            <Txt key={slot.id} size="xsmall">
              <Txt weight="bold" size="xsmall">
                {slot.dateText}
              </Txt>
              {`: ${slot.timesText}`}
              {index < respondedSlots.length - 1 ? ' • ' : ''}
            </Txt>
          ))
        ) : (
          <Txt size="xsmall">No Response</Txt>
        )}
      </Txt>
    </CardRow>
  );
};

const AssessmentSummaryActionStatusLabels: Record<TaskStatusType, string> = {
  [TaskStatusType.notStarted]: 'Not Started',
  [TaskStatusType.inProgress]: 'In Progress',
  [TaskStatusType.completed]: 'Completed',
  [TaskStatusType.cancelled]: 'Cancelled',
  [TaskStatusType.evidenceSubmitted]: 'Evidence Submitted',
};

const getAssessmentSummaryActionStatusText = (status: TaskStatusType | null) =>
  status ? AssessmentSummaryActionStatusLabels[status] : 'No Status';

const getAssessmentSummaryActionStatusColor = (
  status: TaskStatusType | null,
) => {
  switch (status) {
    case TaskStatusType.completed:
      return theme.colors.green;
    case TaskStatusType.inProgress:
      return theme.colors.blue;
    case TaskStatusType.evidenceSubmitted:
      return theme.colors.orange;
    case TaskStatusType.cancelled:
      return theme.colors.red;
    case TaskStatusType.notStarted:
    default:
      return theme.colors.textBlack64;
  }
};

const formatAssessmentSummaryDate = (date: string) =>
  formatDate(new Date(date), UI_DATE_FORMAT_SHORT);

const getAssessmentSummaryCheckedByText = (
  assessmentSummary: BlockManagerAssessmentSummary,
) =>
  assessmentSummary.checkedByPeople
    .map((person) => person.name)
    .filter(filterArrayEmpty)
    .join(', ');

const getAssessmentSummaryActionCountText = (
  assessmentSummary: BlockManagerAssessmentSummary,
) => pluralise(assessmentSummary.actions.length, '1 Action', '{x} Actions', '');

const AssessmentSummaryCardRow: React.FC<{
  assessmentSummary: BlockManagerAssessmentSummary;
  expanded: boolean;
  onToggle: () => void;
}> = ({ assessmentSummary, expanded, onToggle }) => {
  const checkedByText = getAssessmentSummaryCheckedByText(assessmentSummary);
  const screenSize = useScreenSize(true);
  const stackActionRows = screenSize === ScreenSize.small;
  const hasActions = !!assessmentSummary.actions.length;
  return (
    <>
      <CardRow
        icon={ICONS.ChevronRightSolid}
        iconColor={hasActions ? undefined : theme.colors.placeholder}
        iconSize={12}
        iconStyle={{
          transform: [{ rotate: expanded ? '90deg' : '0deg' }],
        }}
        label={`Door Check ${formatAssessmentSummaryDate(
          assessmentSummary.surveyDate,
        )}`}
        labelStyle={{ fontWeight: 'bold' }}
        label2={checkedByText ? `Checked by ${checkedByText}` : undefined}
        onPress={hasActions ? onToggle : undefined}
      >
        <View
          style={{
            flexDirection: stackActionRows ? 'column' : 'row',
          }}
        >
          <View
            style={{
              flex: 1,
              alignItems: stackActionRows ? 'flex-start' : 'flex-end',
              minWidth: 0,
            }}
          >
            {!!assessmentSummary.actionSeveritySummary && (
              <Txt
                size="xsmall"
                numberOfLines={2}
                style={{
                  color: theme.colors.textBlack64,
                  textAlign: stackActionRows ? 'left' : 'right',
                }}
              >
                {assessmentSummary.actionSeveritySummary}
              </Txt>
            )}
            <Txt
              size="xsmall"
              numberOfLines={1}
              style={{
                color: assessmentSummary.actionSeveritySummary
                  ? theme.colors.textBlack64
                  : undefined,
              }}
            >
              {getAssessmentSummaryActionCountText(assessmentSummary)}
            </Txt>
          </View>
        </View>
      </CardRow>
      {expanded && hasActions && (
        <View
          style={{
            backgroundColor: 'white',
            paddingHorizontal: theme.padding,
            paddingBottom: theme.padding,
            paddingTop: theme.padding / 2,
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: theme.padding / 2,
            }}
          >
            {assessmentSummary.actions.map((action) => (
              <Pill
                key={action.uid}
                withIcon
                iconColor={getAssessmentSummaryActionStatusColor(action.status)}
                label={`${getAssessmentSummaryActionStatusText(
                  action.status,
                )}: ${action.title}`}
              />
            ))}
          </View>
        </View>
      )}
    </>
  );
};
export const EngageFlatModal: React.FC<
  ModalScreenProps<ModalScreens.EngageFlat>
> = ({ route }) => {
  const dispatch = useDispatch();

  const { key } = route.params;

  const [activeTab, setActiveTab] = React.useState<'information' | 'messages'>(
    'information',
  );

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  useEsc(handleClose);

  const {
    property: { id: propertyId },
    flatDetails: { name: flatName, id: flatId },
  } = useSelector((state) => selectEngageFlat(state, key))!;

  const handleSendCommunication = React.useCallback(() => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageSendCommunication,
        params: {
          key,
          flatIds: [flatId],
          propertyId,
          contactId: null,
          mode: 'singleFlat',
        },
      }),
    );
  }, [dispatch, flatId, key, propertyId]);

  return (
    <Page
      header={
        <>
          <PageHeader
            title={flatName}
            onPressCancelButton={handleClose}
            buttons={
              <IconButton
                icon={ICONS.PaperPlaneLight}
                onPress={handleSendCommunication}
              />
            }
          />
          <ButtonToggleGroup
            buttons={[
              { text: 'Information', key: 'information' },
              { text: 'Messages', key: 'messages' },
            ]}
            active={activeTab}
            onActiveChange={(newActive) => {
              setTimeout(() => setActiveTab(newActive), 1);
              return false;
            }}
            containerStyle={{
              marginBottom: theme.padding,
              alignSelf: 'stretch',
              marginHorizontal: theme.padding,
            }}
          />
        </>
      }
    >
      <View
        style={[
          activeTab === 'information' ? { flex: 1 } : { display: 'none' },
        ]}
      >
        <FlatModalInformationTab pageKey={key} />
      </View>
      <View
        style={[activeTab === 'messages' ? { flex: 1 } : { display: 'none' }]}
      >
        <EngageFlatModalCommunicationsPage
          pageKey={key}
          contactId={null}
          isActive={activeTab === 'messages'}
        />
      </View>
    </Page>
  );
};

type FlatIssueRow = { key: string; label: string; message: string };

const FlatModalInformationTab: React.FC<{
  pageKey: string;
}> = ({ pageKey: key }) => {
  const dispatch = useDispatch();
  const {
    property,
    flatDetails: flat,
    contacts,
    archivedContacts,
    scheduledVisits,
    assessmentSummaries,
    assessmentSummariesLoading,
    assessmentSummariesInitialLoadFailed,
    assessmentSummariesTotalCount,
    assessmentSummariesPageNumber,
  } = useSelector((state) => selectEngageFlat(state, key))!;
  const [showHistoricScheduledVisits, setShowHistoricScheduledVisits] =
    React.useState(false);
  const [expandedAssessmentSummaryIds, setExpandedAssessmentSummaryIds] =
    React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    if (
      assessmentSummariesPageNumber ||
      assessmentSummariesLoading ||
      assessmentSummariesInitialLoadFailed
    ) {
      return;
    }
    dispatch(
      doEngageFlatAssessmentSummariesLoad({
        key,
        propertyId: property.id,
        flatId: flat.id,
        page: 'reset',
      }),
    );
  }, [
    assessmentSummariesInitialLoadFailed,
    assessmentSummariesLoading,
    assessmentSummariesPageNumber,
    dispatch,
    flat.id,
    key,
    property.id,
  ]);

  // const hasResident = contacts.some((c) => c.isResident);
  const residentsCount = contacts.filter((c) => c.isResident).length;
  const activeScheduledVisits = scheduledVisits.filter(
    (visit) => !visit.historic,
  );
  const historicScheduledVisits = scheduledVisits.filter(
    (visit) => visit.historic,
  );
  const issueDetailRows: FlatIssueRow[] = FlatIssueDetailOrder.filter(
    (issueKey) => !!flat.issues[issueKey],
  ).map((issueKey) => ({
    key: issueKey,
    ...FlatIssueDetailContent[issueKey],
  }));

  const handleOpenContact = (contact: EngageFlatContact) => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageContact,
        params: { key, contactId: contact.id },
      }),
    );
  };
  const handleOpenCreateContact = () => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageContact,
        params: { key, contactId: null },
      }),
    );
  };

  const handleOpenScheduledVisitData = React.useCallback(
    (scheduledVisitId: string) => {
      const scheduledVisit = scheduledVisits.find(
        (sv) => sv.id === scheduledVisitId,
      );
      if (scheduledVisit) {
        dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.EngageFlatScheduledVisitData,
            params: { key, flatName: flat.name, scheduledVisit },
          }),
        );
      }
    },
    [dispatch, flat, key, scheduledVisits],
  );

  const handleToggleAssessmentSummary = React.useCallback(
    (assessmentSummaryId: string) => {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      setExpandedAssessmentSummaryIds((current) => ({
        ...current,
        [assessmentSummaryId]: !current[assessmentSummaryId],
      }));
    },
    [],
  );

  const handleLoadAssessmentSummariesNextPage = React.useCallback(() => {
    dispatch(
      doEngageFlatAssessmentSummariesLoad({
        key,
        propertyId: property.id,
        flatId: flat.id,
        page: 'next',
      }),
    );
  }, [dispatch, flat.id, key, property.id]);

  const handleRetryAssessmentSummariesLoad = React.useCallback(() => {
    dispatch(
      doEngageFlatAssessmentSummariesLoad({
        key,
        propertyId: property.id,
        flatId: flat.id,
        page: 'reset',
      }),
    );
  }, [dispatch, flat.id, key, property.id]);

  const handleUnarchiveContact = React.useCallback(
    (contactId: string) => {
      dispatch(
        doEngageUnarchiveContact({
          key,
          propertyId: property.id,
          flatId: flat.id,
          contactId,
        }),
      );
    },
    [dispatch, flat.id, key, property.id],
  );

  const remainingAssessmentSummariesCount =
    assessmentSummariesTotalCount - assessmentSummaries.length;
  const hasUnloadedAssessmentSummaries = remainingAssessmentSummariesCount > 0;
  const totalAssessmentSummariesLabel = pluralise(
    assessmentSummariesTotalCount,
    '{x} Door Check',
    '{x} Door Checks',
    'Door Checks',
  );

  return (
    <ScrollView>
      <Gap>
        <GapSmall>
          <View
            style={{
              flexDirection: 'row',
              paddingHorizontal: theme.padding,
              gap: theme.padding / 2,
            }}
          >
            <Card style={{ flex: 1 }} marginHorizontal={false}>
              <CardRow
                labelAlignTop
                label={flat.name}
                style={{ borderTopRightRadius: 8 }}
                value={[
                  property.name,
                  property.parentName,
                  ...property.addressLines,
                  property.postcode,
                ]
                  .filter(filterArrayEmpty)
                  .join(', ')}
                valueNumberOfLines={1}
              />
              <View style={{ flexDirection: 'row' }}>
                <CardRow
                  value={rbAddReferencePrefix(flat.reference)}
                  valueNumberOfLines={1}
                  icon={ICONS.RBPlectrumSolid}
                />
                <CardRow
                  value={flat.customReference}
                  valueNumberOfLines={1}
                  icon={ICONS.HashtagLight}
                  style={{ flex: 1 }}
                />
              </View>
            </Card>
            <View
              style={{
                alignSelf: 'stretch',
                aspectRatio: 1,
                minWidth: 64,
              }}
            >
              <Avatar
                data={flat.imageUrl ? { remoteUrl: flat.imageUrl } : null}
                fill
                shape="square"
                resizeMode="cover"
              />
            </View>
          </View>

          {!!issueDetailRows.length && (
            <Card>
              {issueDetailRows.map((issue) => (
                <CardRow
                  key={issue.key}
                  label={issue.label}
                  icon={ICONS.TriangleExclamationDuotoneSolid}
                  iconColor={theme.colors.red}
                  value={issue.message}
                  labelColor={theme.colors.red}
                  valueColor={theme.colors.red}
                  labelAlignTop
                />
              ))}
            </Card>
          )}

          <Card>
            <ViewWithSeparators horizontal>
              <CardRow
                label="HMO"
                labelAlignTop
                style={{ flex: 1 }}
                onPress={() => dispatch(doEngageFlatChangeIsHMO({ key }))}
                value={
                  flat.isHMO === EngageFlatIsHMO.YES
                    ? 'Yes'
                    : flat.isHMO === EngageFlatIsHMO.NO
                      ? 'No'
                      : 'Not Known'
                }
              />
              <CardRow
                label="Institutional Landlord"
                labelAlignTop
                style={{ flex: 1 }}
                onPress={() =>
                  dispatch(doEngageFlatChangeIsCommerciallyOwned({ key }))
                }
                value={
                  flat.isCommerciallyOwned === EngageFlatIsCommerciallyOwned.YES
                    ? 'Yes'
                    : flat.isCommerciallyOwned ===
                        EngageFlatIsCommerciallyOwned.NO
                      ? 'No'
                      : 'Not Known'
                }
              />
            </ViewWithSeparators>
          </Card>
        </GapSmall>

        <GapSmall>
          <Card label="Scheduled Visit Availability">
            {!activeScheduledVisits.length ? (
              <CardRow value="No Scheduled Visits" />
            ) : (
              activeScheduledVisits.map((visit) => (
                <ScheduledVisitCardRow
                  key={visit.id}
                  scheduledVisit={visit}
                  handleOpenScheduledVisitData={handleOpenScheduledVisitData}
                />
              ))
            )}
          </Card>
          {!!historicScheduledVisits.length && !showHistoricScheduledVisits && (
            <Card>
              <CardRow
                label="See Historic Visits"
                labelColor={theme.colors.blue}
                onPress={() => setShowHistoricScheduledVisits(true)}
              />
            </Card>
          )}
        </GapSmall>
        {showHistoricScheduledVisits && (
          <Card
            label={pluralise(
              historicScheduledVisits.length,
              '1 Historic Scheduled Visit',
              '{x} Historic Scheduled Visits',
              'No Historic Scheduled Visits',
            )}
          >
            {historicScheduledVisits.map((visit) => {
              return (
                <ScheduledVisitCardRow
                  key={visit.id}
                  scheduledVisit={visit}
                  handleOpenScheduledVisitData={handleOpenScheduledVisitData}
                />
              );
            })}
          </Card>
        )}

        <Card label={totalAssessmentSummariesLabel}>
          {assessmentSummariesPageNumber === 0 && assessmentSummariesLoading ? (
            <CardRow value="Loading Door Checks..." />
          ) : assessmentSummariesPageNumber === 0 &&
            assessmentSummariesInitialLoadFailed ? (
            <CardRow value="Could Not Load Door Checks" />
          ) : !assessmentSummaries.length ? (
            <CardRow value="No Door Checks" />
          ) : (
            assessmentSummaries.map((assessmentSummary) => (
              <AssessmentSummaryCardRow
                key={assessmentSummary.uid}
                assessmentSummary={assessmentSummary}
                expanded={!!expandedAssessmentSummaryIds[assessmentSummary.uid]}
                onToggle={() =>
                  handleToggleAssessmentSummary(assessmentSummary.uid)
                }
              />
            ))
          )}
        </Card>
        {hasUnloadedAssessmentSummaries ? (
          <View
            style={{
              paddingHorizontal: theme.padding,
              paddingTop: theme.padding,
              alignItems: 'flex-start',
              marginTop: -theme.padding * 2,
            }}
          >
            <Button
              text={pluralise(
                remainingAssessmentSummariesCount,
                '{x} More Summary, Load',
                '{x} More Summaries, Load More',
              )}
              onPress={handleLoadAssessmentSummariesNextPage}
              disabled={assessmentSummariesLoading}
              icon={ICONS.ArrowDownLight}
            />
          </View>
        ) : assessmentSummariesPageNumber === 0 &&
          assessmentSummariesInitialLoadFailed ? (
          <View
            style={{
              padding: theme.padding * 2,
              paddingTop: theme.padding,
              alignItems: 'flex-start',
            }}
          >
            <Button
              text="Retry"
              onPress={handleRetryAssessmentSummariesLoad}
              icon={ICONS.RotateRightLight}
            />
          </View>
        ) : null}

        <GapSmall>
          <Card
            label={pluralise(
              contacts.length,
              '1 Contact',
              '{x} Contacts',
              'Contacts',
            )}
            labelLinkText="Add Contact"
            onLabelLinkPress={handleOpenCreateContact}
          >
            <CardRow
              onPress={() =>
                dispatch(doEngageFlatChangeOccupationStatus({ key }))
              }
              label="Occupation Status"
              value={
                flat.occupationStatus === FlatOccupationStatus.OCCUPIED
                  ? residentsCount
                    ? `Occupied (${pluraliseS(residentsCount, 'Resident')})`
                    : 'Occupied (Residents Not Known)'
                  : flat.occupationStatus === FlatOccupationStatus.UNOCCUPIED
                    ? 'Unoccupied'
                    : 'Not Known'
              }
              valueColor={
                flat.occupationStatus === FlatOccupationStatus.OCCUPIED &&
                !residentsCount
                  ? theme.colors.red
                  : undefined
              }
            />
          </Card>

          <Card>
            {!contacts.length && <CardRow value="No Contacts" />}
            {contacts.map((contact) => (
              <EngageContactCard
                key={contact.id}
                contact={contact}
                flatListContact={false}
                showAddedInfo
                showArchivedInfo
                onPress={handleOpenContact}
                menuOptions={[
                  {
                    text: 'Open Contact',
                    onPress: () => handleOpenContact(contact),
                  },
                  {
                    text: 'Send Message',
                    icon: ICONS.PaperPlaneLight,
                    onPress: () =>
                      dispatch(
                        NAVIGATE_MODAL({
                          screen: ModalScreens.EngageSendCommunication,
                          params: {
                            key,
                            flatIds: [flat.id],
                            propertyId: property.id,
                            contactId: contact.id,
                            mode: 'singleFlat',
                          },
                        }),
                      ),
                  },
                  '-',
                  {
                    text: 'Archive Contact',
                    onPress: () =>
                      dispatch(
                        doEngageArchiveContact({
                          key,
                          propertyId: property.id,
                          flatId: flat.id,
                          contactId: contact.id,
                        }),
                      ),
                    icon: ICONS.TrashCanLight,
                    destructive: true,
                  },
                ]}
              />
            ))}
          </Card>

          {archivedContacts === null && (
            <Card>
              <CardRow
                label={'See Archived Contacts'}
                labelColor={theme.colors.blue}
                onPress={() =>
                  dispatch(
                    doEngageFlatArchivedContactsLoad({
                      key,
                      propertyId: property.id,
                      flatId: flat.id,
                    }),
                  )
                }
              />
            </Card>
          )}
        </GapSmall>

        {archivedContacts !== null && (
          <Card
            label={pluralise(
              archivedContacts.length,
              '1 Archived Contact',
              '{x} Archived Contacts',
              'No Archived Contacts',
            )}
          >
            {!archivedContacts.length && (
              <CardRow value="No Archived Contacts" />
            )}
            {archivedContacts.map((contact) => (
              <EngageContactCard
                key={contact.id}
                contact={contact}
                flatListContact={false}
                showAddedInfo
                showArchivedInfo
                menuOptions={[
                  {
                    text: 'Unarchive Contact',
                    onPress: () => handleUnarchiveContact(contact.id),
                  },
                ]}
              />
            ))}
          </Card>
        )}
      </Gap>
    </ScrollView>
  );
};
